/**
 * Per-EPC recent-checkout lockout (ported from gen5-pwa 2026-08-04).
 *
 * Guards against a race where the smartbox3 / md_rfid status hasn't
 * propagated back to a second operator's lookup yet, so the tag
 * still shows as ON_HAND and a duplicate MC succeeds against a
 * tag that's already out. Store the EPCs we just checked out in
 * localStorage with a 10-minute TTL; block re-adding within that
 * window. Also cleared on MI so a legitimate check-in / re-checkout
 * cycle isn't gated by the lockout.
 *
 * Same TTL as gen5-pwa (`RECENT_CHECKOUT_TTL_MS = 10 * 60 * 1000`).
 * Same storage-key convention (namespaced under `mttr:`).
 */

const STORAGE_KEY = "mttr:recentCheckouts";
export const RECENT_CHECKOUT_TTL_MS = 10 * 60 * 1000;

type Store = Record<string, number>; // epc -> epoch ms

function _read(): Store {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed as Store : {};
  } catch {
    return {};
  }
}

function _write(s: Store): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Storage full / disabled — best-effort feature, not blocking.
  }
}

function _prune(s: Store, now: number): Store {
  const out: Store = {};
  for (const [epc, ts] of Object.entries(s)) {
    if (typeof ts === "number" && now - ts < RECENT_CHECKOUT_TTL_MS) {
      out[epc] = ts;
    }
  }
  return out;
}

/**
 * If the EPC was checked out in the last TTL, return the milliseconds
 * remaining until the lockout expires. Otherwise return 0.
 */
export function msRemainingLockout(epc: string): number {
  if (!epc) return 0;
  const now = Date.now();
  const store = _prune(_read(), now);
  _write(store);
  const key = epc.trim().toUpperCase();
  const ts = store[key];
  if (typeof ts !== "number") return 0;
  const remaining = RECENT_CHECKOUT_TTL_MS - (now - ts);
  return remaining > 0 ? remaining : 0;
}

/** Record a batch of EPCs as just-checked-out. */
export function recordCheckouts(epcs: string[]): void {
  if (!epcs.length) return;
  const now = Date.now();
  const store = _prune(_read(), now);
  for (const raw of epcs) {
    const k = raw.trim().toUpperCase();
    if (k) store[k] = now;
  }
  _write(store);
}

/** Clear a batch of EPCs from the lockout — used on successful MI. */
export function clearCheckouts(epcs: string[]): void {
  if (!epcs.length) return;
  const store = _prune(_read(), Date.now());
  let changed = false;
  for (const raw of epcs) {
    const k = raw.trim().toUpperCase();
    if (k in store) {
      delete store[k];
      changed = true;
    }
  }
  if (changed) _write(store);
}

/** Human-readable "X min Y sec" for the block message. */
export function formatLockoutRemaining(ms: number): string {
  const totalSec = Math.max(1, Math.round(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec}s`;
  if (sec === 0) return `${min}m`;
  return `${min}m ${sec}s`;
}
